// hochzaehlen.ts — Zahlen zaehlen beim Einscrollen hoch.
//
// Uebernommen aus dem 420Pharma-Projekt (src/scripts/countup.ts), dort mit GSAP
// geloest. Hier ohne, obwohl GSAP in diesem Projekt vorhanden waere: ein Zaehler
// braucht keinen Tween, und so bleibt der Baustein unabhaengig davon, ob GSAP
// auf einer Seite ueberhaupt geladen wird. Die beiden wichtigen Eigenschaften
// von damals bleiben erhalten:
//
// 1. Der fertige Wert steht sofort da. Das Element traegt von Anfang an den
//    Zielwert, nicht null. Faellt JavaScript aus, steht trotzdem die richtige
//    Zahl auf der Seite — nur eben ohne Animation.
// 2. Wer "reduzierte Bewegung" eingestellt hat, bekommt gar keine Animation.
//    Nicht eine schnellere, sondern keine.
import { formatiere } from './zerlege';

/** So lange laeuft der Zaehler. Kurz genug, um nicht zu warten. */
const DAUER_MS = 1400;

/** Weich auslaufend, damit die letzten Stellen lesbar zur Ruhe kommen. */
function abbremsen(fortschritt: number): number {
  return 1 - Math.pow(1 - fortschritt, 3);
}

/**
 * Laesst die Zahl in `element` hochzaehlen, sobald es sichtbar wird.
 *
 * @param element Das Element, dessen Text die Zahl traegt.
 * @param ziel Der Endwert, oder null wenn im Text gar keine Zahl steckt.
 * @param dezimalstellen Nachkommastellen, wie sie in der Quelle standen.
 * @returns Eine Funktion zum Abraeumen, oder null wenn nichts zu tun war.
 */
export function hochzaehlen(
  element: HTMLElement,
  ziel: number | null,
  dezimalstellen = 0,
): (() => void) | null {
  if (ziel === null) return null;

  // Alles, was hier fehlen kann, fuehrt zum selben Ergebnis: die fertige Zahl
  // bleibt stehen. Das ist der richtige Ausfallweg, nicht ein Zaehler bei null.
  if (typeof IntersectionObserver === 'undefined') return null;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

  let laeuft = 0;
  let start: number | null = null;

  const schritt = (jetzt: number) => {
    if (start === null) start = jetzt;
    const fortschritt = Math.min((jetzt - start) / DAUER_MS, 1);

    if (fortschritt < 1) {
      element.textContent = formatiere(ziel * abbremsen(fortschritt), dezimalstellen);
      laeuft = window.requestAnimationFrame(schritt);
    } else {
      // Am Ende exakt den Zielwert setzen, nicht den gerundeten Zwischenwert.
      element.textContent = formatiere(ziel, dezimalstellen);
      laeuft = 0;
    }
  };

  const beobachter = new IntersectionObserver(
    (eintraege) => {
      if (!eintraege.some((eintrag) => eintrag.isIntersecting)) return;

      beobachter.disconnect();
      element.textContent = formatiere(0, dezimalstellen);
      laeuft = window.requestAnimationFrame(schritt);
    },
    { threshold: 0.4 },
  );

  beobachter.observe(element);

  return () => {
    beobachter.disconnect();
    if (laeuft) window.cancelAnimationFrame(laeuft);
    // Bricht der Lauf mittendrin ab, darf keine halbe Zahl stehen bleiben.
    element.textContent = formatiere(ziel, dezimalstellen);
  };
}
